// Aintree — upcoming SIL home (Melbourne's growth corridor).
export const AINTREE = {
  slug: "aintree-house",
  href: "/homes/aintree-house",
  name: "The Aintree House",
  suburb: "Aintree",
  state: "VIC",
  short: "SIL Home · Aintree, VIC · Coming soon",

  status: "upcoming", // "available" | "upcoming" | "filled"

  headline: "A brand-new SIL home, built for calm modern living.",
  oneLiner:
    "Modern, light-filled and freshly finished — Aintree is an Alpha SIL home opening in Melbourne's western growth corridor.",

  badges: [
    { label: "SIL House", tone: "purple" },
    { label: "1:1 & 1:2 support only", tone: "green" },
    { label: "Coming soon · Register interest", tone: "neutral" },
  ],

  specs: [
    { k: "Service", v: "Supported Independent Living" },
    { k: "Support ratio", v: "1:1 and 1:2 only" },
    { k: "Layout", v: "Two storey · upstairs retreat" },
    { k: "Location", v: "Aintree, VIC" },
    { k: "Availability", v: "Pre-launch — register interest now" },
    { k: "Support hours", v: "24/7 on-call" },
  ],

  features: [
    "Modern two-storey design",
    "Double-garage off-street parking",
    "Engineered timber-look floors",
    "Generous open-plan living",
    "Stone-look kitchen with island bench",
    "Marble-feature ensuite",
    "Freestanding deep bathtub",
    "Frameless glass walk-in shower",
    "Double basin vanity",
    "Master bedroom with walk-in robe",
    "Direct ensuite access from main bedroom",
    "Upstairs retreat / second living",
    "Recessed LED lighting throughout",
    "Soft-tone carpets to bedrooms",
    "Cathedral-style raked roofline",
    "Covered alfresco patio",
    "Generous fully-fenced rear yard",
    "Private rear yard access",
  ],

  ndis: {
    summary:
      "Delivered under the NDIS as Supported Independent Living (SIL). Aintree is purpose-set up for participants who want a quieter, modern environment with consistent close support.",
    points: [
      "Services delivered as 1:1 or 1:2 support ratios only — never higher.",
      "Staff matched to each resident's preferences, culture and routines.",
      "Overnight and 24/7 on-call availability.",
      "Coordinated clinical oversight on request.",
      "Pre-launch — capacity is being prioritised for early enquiries.",
    ],
  },

  description: [
    "Set across two well-proportioned storeys, the Aintree house is a brand-new build finished in a calm, modern palette of pale walls, engineered timber-look flooring and stone-look benchtops.",
    "The downstairs open-plan living and dining flows out through a wide sliding door to a covered alfresco patio and a generous, fully-fenced rear lawn — bright, easy-care and made for everyday calm.",
    "The kitchen sits at the heart of the home with a generous island bench, a striking veined-stone splashback, and quality appliances that genuinely make daily living smooth.",
    "Upstairs, a separate retreat offers extra living space with a soaring cathedral-style raked roofline — perfect for quiet time away from the main level — with large windows and feature timber balustrades creating a warm contrast against the neutral palette.",
    "The master bedroom is generously sized and softly carpeted, opening directly to its own ensuite with a freestanding deep bath, double-basin vanity and a frameless glass walk-in shower. A timber-slat wardrobe brings a quiet, premium accent.",
    "Both bathrooms are finished to a premium standard: marble-look tiling, double-basin vanities, freestanding deep bathtubs and frameless glass walk-in showers — calm, dignified, and functional.",
  ],

  // Real-estate-style quick facts row (verified by visible photos only).
  quickFacts: [
    { Icon: "Layers", k: "Layout", v: "Two storey" },
    { Icon: "Bath", k: "Bathrooms", v: "2 premium" },
    { Icon: "Car", k: "Parking", v: "Double garage" },
    { Icon: "Users", k: "Support ratio", v: "1:1 & 1:2 only" },
    { Icon: "Calendar", k: "Status", v: "Coming soon" },
  ],

  images: [
    { src: "/homes/aintree/1.jpg", alt: "Aintree front exterior — modern two-storey home with double garage and timber feature door" },
    { src: "/homes/aintree/2.jpg", alt: "Bright marble bathroom with double basin vanity and freestanding bathtub" },
    { src: "/homes/aintree/3.jpg", alt: "Upstairs retreat / second living area with large windows and timber balustrade" },
    { src: "/homes/aintree/4.jpg", alt: "Premium ensuite with double vanity, freestanding tub and frameless glass shower" },
    { src: "/homes/aintree/5.jpg", alt: "Open-plan living looking up to mezzanine, kitchen with stone island and sliding door to yard" },
    { src: "/homes/aintree/6.jpg", alt: "Rear exterior — cathedral-style upper roof, covered alfresco patio and large fully-fenced rear lawn" },
    { src: "/homes/aintree/7.jpg", alt: "Master bedroom with timber-slat wardrobe and direct access to ensuite with freestanding bath" },
  ],

  story: {
    designedFor: {
      eyebrow: "Designed for comfort",
      title: "A brand-new home, ready to live in.",
      body: "Aintree was built fresh — quiet palette, soft carpets, full sun and clean architectural lines. Move in without renovation, retrofit or compromise.",
      image: "/homes/aintree/5.jpg",
    },
    indoorOutdoor: {
      eyebrow: "Indoor & outdoor living",
      title: "From the kitchen to the lawn — one calm flow.",
      body: "Open-plan downstairs spills out to a covered alfresco patio and a generous, fully-fenced rear yard. Easy to use, easy to live with.",
      image: "/homes/aintree/6.jpg",
    },
    selfCare: {
      eyebrow: "Premium private suites",
      title: "A master bedroom that opens to its own ensuite.",
      body: "Soft carpet, timber-slat wardrobe, and a direct line into a marble-look ensuite with freestanding bath, double basin and walk-in shower.",
      image: "/homes/aintree/7.jpg",
    },
  },

  accessibility: [
    { title: "Single-level living option", text: "Daily living space accessible without stairs on the ground floor." },
    { title: "Wide internal doorways", text: "Comfortable for mobility aids throughout." },
    { title: "Walk-in glass showers", text: "Frameless glass, level entry — easy on every shift." },
    { title: "Twin-vanity bathrooms", text: "Reduce wait times, support 1:1 + 1:2 routines." },
    { title: "Soft acoustics", text: "Carpet upstairs absorbs sound — quieter sensory load." },
    { title: "Outdoor flat lawn", text: "Fully fenced — suitable for assistive equipment and pets." },
  ],

  idealFor: [
    "Participants ready to start in a brand-new home",
    "People who benefit from low-stim modern interiors",
    "Adults who enjoy gardens, sun and outdoor time",
    "Households suited to 1:1 and 1:2 support delivery",
    "Anyone wanting a premium, quietly designed space",
  ],

  whyLove: [
    "It feels brand-new — because it is.",
    "The kitchen is the social heart.",
    "Bathrooms feel hotel-grade.",
    "The yard makes it home — not a unit.",
  ],

  lifestyle: {
    headline: "Melbourne's western growth corridor.",
    blurb:
      "Aintree is part of a fresh, family-oriented neighbourhood with parks, schools and brand-new community amenity within minutes.",
    points: [
      { k: "Parks & open space", v: "Multiple new reserves walking distance" },
      { k: "Shopping", v: "Woodlea Town Centre nearby" },
      { k: "Healthcare", v: "GP, pharmacy + allied health a short drive" },
      { k: "Connection", v: "Direct access to the Western Freeway corridor" },
    ],
  },
};
